/**
 * Modelos e Interfaces Tipadas para o Módulo de Faturação Eletrónica (SecurPro).
 * Baseados rigorosamente nos Schemas do Swagger e nas respostas reais da API.
 */

export enum EnumDocumentStatus {
  Guardada = 'Guardada',
  Submetida = 'Submetida',
  Aceite = 'Aceite',
  Rejeitada = 'Rejeitada',
}

export type DocumentStatus = 'Guardada' | 'Submetida' | 'Aceite' | 'Rejeitada';

export enum EnumPaymentStatus {
  NaoPago = 'NaoPago',
  ParcialmentePago = 'ParcialmentePago',
  PagoTotalmente = 'PagoTotalmente',
}

export enum FormatoDocumentoPdf {
  A4 = 1,
  Ticket = 2,
}

export enum EnumTipoProduto {
  Artigo = 1,
  Servico = 2,
}

export interface FeTipoDocumento {
  id: number;
  codigo: string; // "FT", "FR", "RC", "NC"
  descricao?: string;
  designacao?: string;
  ativo?: boolean;
}

export interface FeProduto {
  id: number;
  tipoProduto: EnumTipoProduto | number;
  productCode: string;
  productDescription: string;
  unitOfMeasure: string;
  unitPrice?: number;
  preco?: number;
  ativo?: boolean;
}

export interface FeTaxCode {
  id: number;
  taxId: number;
  taxCode: string; // "NOR", "INT", "RED", "ISE", "OUT"
  taxCountryRegion: string; // "AO"
  taxPercentage: number; // 14, 0, etc.
  codigo?: string;
  descricao?: string;
}

export interface FeTaxExemptionCode {
  id: number;
  taxExemptionCode: string; // "M00", "M02", etc.
  ativo: boolean;
  idTaxCode: number;
  codigo?: string;
  descricao?: string;
}

export interface FeSerie {
  id: number;
  seriesCode: string; // ex: "FT4326S12697N"
  documentType: string; // "FT", "FR"
  seriesYear: number; // 2026
  establishmentNumber: string; // "SEDE"
  seriesContingencyIndicator: boolean;
  authorizedQuantity?: number;
  lastGeneratedNo?: number;
  active?: boolean;
}

export interface FeDocumentoLinhaTax {
  idDocumentTax?: number;
  taxCode: string;
  taxType: string; // "IVA"
  taxCountryRegion: string; // "AO"
  taxPercentage: number;
  taxContribution: number;
  idTaxExemption?: number | null;
  taxExemptionCode?: string | null;
}

export interface FeDocumentoLinha {
  lineNumber?: string | number;
  idProduct: number;
  quantity: number;
  unitPrice: number;
  unitPriceBase?: number;
  unitOfMeasure?: string;
  debitAmount?: number;
  creditAmount?: number;
  settlementAmount?: number;
  taxes?: FeDocumentoLinhaTax[];
  idTaxCode?: number;
  precoComImpostoIncluido?: boolean;
  idTaxExemptionCode?: number | null;
  productCode?: string;
  productDescription?: string;
  description?: string;
  netTotal?: number;
  taxPayable?: number;
  grossTotal?: number;
  discountPercentage?: number;
}

export interface FeDocumentoClienteInfo {
  id?: number;
  nome?: string;
  nif?: string;
  email?: string;
  endereco?: string;
  numTelefone?: string;
  nomeResponsavel?: string;
  sector?: string;
}

export interface FeDocumento {
  id: number;
  documentNo: string;
  estado: EnumDocumentStatus | string;
  documentStatus?: string;
  requestId: string | null;
  rejectedDocumentNo: string | null;
  idCliente: number;
  idTipoDocumento: number;
  documentDate?: string;
  dataEmissao?: string;
  netTotal: number;
  taxPayable: number;
  grossTotal: number;
  paymentStatus?: EnumPaymentStatus | string | null;
  paymentReference?: string | null;
  errorList?: string[];
  lines?: FeDocumentoLinha[];
  cliente?: FeDocumentoClienteInfo;
  tipoDocumento?: FeTipoDocumento | string;
  seriesContingencyIndicator?: boolean;
}

export interface DocumentLineRequestDto {
  idProduct: number;
  quantity: number;
  unitPrice: number;
  idTaxCode: number;
  precoComImpostoIncluido: boolean;
  idTaxExemptionCode?: number | null;
}

export interface CriarDocumentoRequestDto {
  idTipoDocumento: number;
  idCliente: number;
  seriesContingencyIndicator: boolean;
  lines: DocumentLineRequestDto[];
}

export interface FeDocumentoInput extends CriarDocumentoRequestDto {}

export interface FeSaldoDocumento {
  idDocumento: number;
  numeroDocumento: string;
  dataDocumento: string;
  totalDocumento: number;
  valorPago: number;
  saldoEmDivida: number;
  estadoPagamento: string;
  estadoPagamentoId?: number;
  paymentReference?: string | null;
}

export interface FeReciboItem {
  id: number;
  documentNo: string;
  documentDate: string;
  valorPago: number;
}

export interface FeClienteSaldoEmDivida {
  idCliente: number;
  saldoEmDivida: number;
  quantidadeFaturasEmDivida: number;
  faturas: FeSaldoDocumento[];
}

export interface FeDocumentoFiltros {
  DocumentNo?: string;
  Estado?: string;
  IdCliente?: number;
  RequestId?: string;
  IdTipoDocumento?: number;
  DataInicial?: string;
  DataFinal?: string;
  Page?: number;
  PageSize?: number;
  // Campos alternativos em camelCase
  documentNo?: string;
  estado?: string;
  idCliente?: number;
  requestId?: string;
  idTipoDocumento?: number;
  dataInicial?: string;
  dataFinal?: string;
  page?: number;
  pageSize?: number;
}

export interface FePagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
}

/** Compatibilidade com código pré-existente */
export interface FePagedResponse<T> {
  data: T[];
  totalRecords: number;
  page: number;
  pageSize: number;
}

export interface SimularPagamentoRequestDto {
  amount: number;
}

export interface SolicitarSerieRequestDto {
  idTipoDocumento: number;
  seriesYear: number;
  establishmentNumber: string;
  seriesContingencyIndicator: boolean;
}

export interface ProductRequestDto {
  tipoProduto: EnumTipoProduto | number;
  productDescription: string;
  unitOfMeasure: string;
}

export interface TaxCodeRequestDto {
  taxId: number;
  taxCode: string;
  taxCountryRegion: string;
  taxPercentage: number;
}

export interface TaxExemptionCodeRequestDto {
  taxExemptionCode: string;
  ativo: boolean;
  idTaxCode: number;
}

export interface DocumentTypeRequestDto {
  codigo: string;
  descricao: string;
}
